import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';

const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase();

const Q = (n) => `Q${(Number(n)||0).toFixed(2)}`;

export default function DashboardGeneral() {
  const { user } = useAuth();
  // Estado y filtros SIEMPRE deben declararse antes de cualquier return
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const roleKey = useMemo(() => {
    const raw = user?.rol ?? user?.role ?? user?.tipoUsuario ?? '';
    return String(raw)
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
  }, [user]);
  const allowed = useMemo(() => ['admingeneral', 'admintec', 'admincomercial', 'admin', 'despacho', 'serviciocliente', 'vendedor'], []);
  const canSee = allowed.includes(roleKey);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    (async () => {
      try {
        // Esperar a que AuthContext cargue al usuario para evitar mostrar "no permisos" en falso
        if (!user) {
          // No cambiamos loading aquí: dejamos el loader hasta que haya usuario
          return;
        }
        if (!canSee) { if (alive) { setData(null); setLoading(false); } return; }
        const sp = new URLSearchParams();
        if (from) sp.set('from', from);
        if (to) sp.set('to', to);
        const url = `${API_BASE}/api/admin/estadisticas${sp.toString() ? `?${sp.toString()}` : ''}`;
        const r = await fetch(url, { credentials: 'include' });
        const j = await r.json();
        if (!r.ok || j.ok === false) throw new Error(j?.msg || `HTTP ${r.status}`);
        if (!alive) return;
        setData(j);
      } catch (e) {
        if (!alive) return;
        setErr(e.message || 'Error cargando estadísticas');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [from, to, canSee, user]);

  // Mientras no haya usuario en AuthContext, mostramos un loader en lugar de "no tienes permisos"
  if (!user) return <div className="p-4">Cargando…</div>;

  if (!canSee) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-2xl border bg-white p-4 shadow-sm text-slate-700">
          No tienes permisos para ver el Dashboard.
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-4">Cargando…</div>;
  if (err) return <div className="p-4 text-red-700 bg-red-50 border border-red-200 rounded">{err}</div>;
  if (!data) return <div className="p-4">Sin datos</div>;

  const ventas = data.ventas || {}; // { semana, mes, anio }
  const topProductos = data.topProductos || {}; // { semana, mes, anio }
  const topClientes = data.topClientes || {}; // { semana, mes, anio }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <h1 className="text-2xl font-bold">Dashboard general</h1>

      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="text-sm font-semibold text-gray-700 mb-2">Búsqueda por rango específico (opcional)</div>
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-600">Desde</label>
            <input type="date" value={from} onChange={e=> setFrom(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Hasta</label>
            <input type="date" value={to} onChange={e=> setTo(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" />
          </div>
          <div className="sm:col-span-3 text-xs text-slate-500">
            Los indicadores de arriba siempre muestran los datos actuales. Si seleccionas un rango, se agregará una sección "Rango personalizado" con resultados del periodo.
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card title="Ventas semanales">
          <Metric value={Q(ventas.semana)} subtitle="Últimos 7 días" />
        </Card>
        <Card title="Ventas mensuales">
          <Metric value={Q(ventas.mes)} subtitle="Últimos 30 días" />
        </Card>
        <Card title="Ventas anuales">
          <Metric value={Q(ventas.anio)} subtitle="Últimos 365 días" />
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Top productos por cantidad (semana)">
          <div className="grid md:grid-cols-2 gap-4 items-start">
            <PieChart
              data={Array.isArray(topProductos.semana) ? topProductos.semana : []}
              labelKey="nombre"
              valueKey="qty"
            />
            <div className="space-y-2">
              <Legend data={Array.isArray(topProductos.semana) ? topProductos.semana : []} labelKey="nombre" valueKey="qty" />
              <div className="text-xs text-slate-500">Top 10 de la última semana</div>
              <div className="mt-2">
                <TopTable rows={topProductos.semana} type="producto" />
              </div>
            </div>
          </div>
        </Card>
        <Card title="Top clientes por compras (semana)">
          <div className="grid md:grid-cols-2 gap-4 items-start">
            <PieChart
              data={Array.isArray(topClientes.semana) ? topClientes.semana : []}
              labelKey="cliente"
              valueKey="pedidos"
              unitLabel="compras"
            />
            <div className="space-y-2">
              <Legend data={Array.isArray(topClientes.semana) ? topClientes.semana : []} labelKey="cliente" valueKey="pedidos" unitLabel="compras" />
              <div className="text-xs text-slate-500">Top 10 de la última semana</div>
              <div className="mt-2">
                <TopClientes rows={topClientes.semana} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Top productos por cantidad (mes)">
          <div className="grid md:grid-cols-2 gap-4 items-start">
            <BarChart
              data={Array.isArray(topProductos.mes) ? topProductos.mes : []}
              labelKey="nombre"
              valueKey="qty"
            />
            <div className="space-y-2">
              <Legend data={Array.isArray(topProductos.mes) ? topProductos.mes : []} labelKey="nombre" valueKey="qty" />
              <div className="text-xs text-slate-500">Top 10 del último mes</div>
              <div className="mt-2">
                <TopTable rows={topProductos.mes} type="producto" />
              </div>
            </div>
          </div>
        </Card>
        <Card title="Top clientes por compras (mes)">
          <div className="grid md:grid-cols-2 gap-4 items-start">
            <BarChart
              data={Array.isArray(topClientes.mes) ? topClientes.mes : []}
              labelKey="cliente"
              valueKey="pedidos"
              unitLabel="compras"
            />
            <div className="space-y-2">
              <Legend data={Array.isArray(topClientes.mes) ? topClientes.mes : []} labelKey="cliente" valueKey="pedidos" unitLabel="compras" />
              <div className="text-xs text-slate-500">Top 10 del último mes</div>
              <div className="mt-2">
                <TopClientes rows={topClientes.mes} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Top productos por cantidad (año)">
          <div className="grid md:grid-cols-2 gap-4 items-start">
            <LineChart
              data={Array.isArray(topProductos.anio) ? topProductos.anio : []}
              labelKey="nombre"
              valueKey="qty"
            />
            <div className="space-y-2">
              <Legend data={Array.isArray(topProductos.anio) ? topProductos.anio : []} labelKey="nombre" valueKey="qty" />
              <div className="text-xs text-slate-500">Top 10 del último año</div>
              <div className="mt-2">
                <TopTable rows={topProductos.anio} type="producto" />
              </div>
            </div>
          </div>
        </Card>
        <Card title="Top clientes por compras (año)">
          <div className="grid md:grid-cols-2 gap-4 items-start">
            <LineChart
              data={Array.isArray(topClientes.anio) ? topClientes.anio : []}
              labelKey="cliente"
              valueKey="pedidos"
            />
            <div className="space-y-2">
              <Legend data={Array.isArray(topClientes.anio) ? topClientes.anio : []} labelKey="cliente" valueKey="pedidos" unitLabel="compras" />
              <div className="text-xs text-slate-500">Top 10 del último año</div>
              <div className="mt-2">
                <TopClientes rows={topClientes.anio} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {!!data.custom && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Rango personalizado ({data.custom.from} → {data.custom.to})</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Card title="Ventas en rango">
              <Metric value={Q(data.custom.ventas)} subtitle="Suma total del rango" />
            </Card>
            <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
              <Card title="Top productos (rango)"><TopTable rows={data.custom.topProductos} type="producto" /></Card>
              <Card title="Top clientes (rango)"><TopClientes rows={data.custom.topClientes} /></Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
      <div className="text-sm font-semibold text-gray-700 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Metric({ value, subtitle }) {
  return (
    <div>
      <div className="text-3xl font-extrabold">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
    </div>
  );
}

function TopTable({ rows, type }) {
  const Q = (n) => `Q${(Number(n)||0).toFixed(2)}`;
  const list = Array.isArray(rows) ? rows : [];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            <th className="py-2 pr-4">{type === 'producto' ? 'Producto' : 'Cliente'}</th>
            <th className="py-2 pr-4">Cantidad</th>
            <th className="py-2 pr-0 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r, idx) => (
            <tr key={idx} className="border-b last:border-0">
              <td className="py-2 pr-4">{r.nombre || r.producto || r.cliente || '—'}</td>
              <td className="py-2 pr-4">{r.qty || r.pedidos || 0}</td>
              <td className="py-2 pr-0 text-right">{Q(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TopClientes({ rows }) {
  const Q = (n) => `Q${(Number(n)||0).toFixed(2)}`;
  const list = Array.isArray(rows) ? rows : [];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            <th className="py-2 pr-4">Cliente</th>
            <th className="py-2 pr-4">Compras</th>
            <th className="py-2 pr-0 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r, idx) => (
            <tr key={idx} className="border-b last:border-0">
              <td className="py-2 pr-4">{r.cliente}</td>
              <td className="py-2 pr-4">{r.pedidos}</td>
              <td className="py-2 pr-0 text-right">{Q(r.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// === Pie Chart (SVG) sin dependencias ===
function PieChart({ data, labelKey = 'label', valueKey = 'value', size = 220, innerRatio = 0.55, unitLabel = 'uds' }) {
  const items = Array.isArray(data) ? data : [];
  const vals = items.map((d) => Number(d?.[valueKey] || 0)).filter((n) => n > 0);
  const total = vals.reduce((a, b) => a + b, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4; // pequeño padding
  const rInner = Math.max(0, Math.min(r - 4, r * innerRatio));
  const palette = [
    '#0ea5e9','#22c55e','#f59e0b','#ef4444','#8b5cf6',
    '#14b8a6','#f97316','#84cc16','#06b6d4','#a78bfa',
  ];
  let angle = -Math.PI / 2; // iniciar arriba
  const arcs = [];
  if (total > 0) {
    items.forEach((d, i) => {
      const v = Number(d?.[valueKey] || 0);
      if (v <= 0) return;
      const frac = v / total;
      const delta = Math.max(0, Math.min(2 * Math.PI, frac * 2 * Math.PI));
      const start = angle;
      const end = angle + delta;
      angle = end;
      const largeArc = delta > Math.PI ? 1 : 0;
      const color = palette[i % palette.length];
      const x0 = cx + r * Math.cos(start);
      const y0 = cy + r * Math.sin(start);
      const x1 = cx + r * Math.cos(end);
      const y1 = cy + r * Math.sin(end);
      if (rInner > 0) {
        const x0i = cx + rInner * Math.cos(end);
        const y0i = cy + rInner * Math.sin(end);
        const x1i = cx + rInner * Math.cos(start);
        const y1i = cy + rInner * Math.sin(start);
        const dPath = [
          `M ${x0} ${y0}`,
          `A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`,
          `L ${x0i} ${y0i}`,
          `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x1i} ${y1i}`,
          'Z',
        ].join(' ');
        arcs.push({ d: dPath, color });
      } else {
        const dPath = [
          `M ${cx} ${cy}`,
          `L ${x0} ${y0}`,
          `A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`,
          'Z',
        ].join(' ');
        arcs.push({ d: dPath, color });
      }
    });
  }

  return (
    <div className="flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.25" />
          </filter>
        </defs>
        <g filter="url(#softShadow)">
          {arcs.map((a, idx) => (
            <path key={idx} d={a.d} fill={a.color} />
          ))}
        </g>
        {rInner > 0 && (
          <circle cx={cx} cy={cy} r={rInner - 1} fill="#fff" />
        )}
        {rInner > 0 && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize="12" fill="#334155">
            {total} {unitLabel}
          </text>
        )}
      </svg>
    </div>
  );
}

function Legend({ data, labelKey = 'label', valueKey = 'value', unitLabel = 'uds' }) {
  const items = Array.isArray(data) ? data : [];
  const palette = [
    '#0ea5e9','#22c55e','#f59e0b','#ef4444','#8b5cf6',
    '#14b8a6','#f97316','#84cc16','#06b6d4','#a78bfa',
  ];
  return (
    <div className="space-y-1 text-sm">
      {items.map((it, idx) => (
        <div key={idx} className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: palette[idx % palette.length] }} />
            <span className="truncate text-slate-700" title={String(it?.[labelKey] || '')}>
              {String(it?.[labelKey] || '')}
            </span>
          </div>
          <div className="text-slate-600 tabular-nums">{Number(it?.[valueKey] || 0)} {unitLabel}</div>
        </div>
      ))}
    </div>
  );
}

// === Bar Chart horizontal (SVG) ===
function BarChart({ data, labelKey = 'label', valueKey = 'value', width = 360, barH = 18, gap = 8, padding = 12, unitLabel = 'uds' }) {
  const items = Array.isArray(data) ? data : [];
  const vals = items.map((d) => Number(d?.[valueKey] || 0));
  const max = Math.max(1, ...vals);
  const height = padding * 2 + items.length * (barH + gap) - gap;
  const palette = ['#0ea5e9','#22c55e','#f59e0b','#ef4444','#8b5cf6','#14b8a6','#f97316','#84cc16','#06b6d4','#a78bfa'];
  const labelW = 120; // ancho para etiquetas
  const plotW = Math.max(80, width - labelW - padding * 2);
  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {items.map((it, idx) => {
          const y = padding + idx * (barH + gap);
          const v = Number(it?.[valueKey] || 0);
          const w = (v / max) * plotW;
          const color = palette[idx % palette.length];
          const label = String(it?.[labelKey] || '');
          return (
            <g key={idx} transform={`translate(${padding}, ${y})`}>
              <text x={0} y={barH/2} dominantBaseline="middle" className="fill-slate-700" fontSize="10" style={{ maxWidth: labelW }}>
                {label.length > 18 ? label.slice(0, 17) + '…' : label}
              </text>
              <rect x={labelW + 8} y={0} width={plotW} height={barH} fill="#e5e7eb" rx="3" />
              <rect x={labelW + 8} y={0} width={Math.max(2, w)} height={barH} fill={color} rx="3" />
              <text x={labelW + 12 + Math.max(2, w)} y={barH/2} dominantBaseline="middle" className="fill-slate-600" fontSize="10">
                {v} {unitLabel}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// === Line Chart (SVG) con puntos ===
function LineChart({ data, labelKey = 'label', valueKey = 'value', width = 360, height = 220 }) {
  const items = Array.isArray(data) ? data : [];
  const vals = items.map((d) => Number(d?.[valueKey] || 0));
  const maxV = Math.max(1, ...vals);
  const margin = { top: 12, right: 16, bottom: 36, left: 32 };
  const w = Math.max(160, width);
  const h = Math.max(160, height);
  const plotW = w - margin.left - margin.right;
  const plotH = h - margin.top - margin.bottom;
  const palette = ['#0ea5e9'];
  const color = palette[0];
  const n = Math.max(1, items.length);
  const xFor = (i) => (n === 1 ? plotW/2 : (i/(n-1)) * plotW);
  const yFor = (v) => plotH - (v/maxV) * plotH;
  const points = items.map((it, i) => ({ x: xFor(i), y: yFor(Number(it?.[valueKey] || 0)), label: String(it?.[labelKey] || '') }));
  const pathD = points.reduce((acc, p, i) => acc + (i===0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), '');
  // ticks Y simples 0, 25%, 50%, 75%, 100%
  const ticks = [0, .25, .5, .75, 1].map(t => ({ y: yFor(t*maxV), v: Math.round(t*maxV) }));
  return (
    <div className="overflow-x-auto">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {/* grid y eje Y */}
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={0} y1={t.y} x2={plotW} y2={t.y} stroke="#e5e7eb" strokeWidth={1} />
              <text x={-8} y={t.y} textAnchor="end" dominantBaseline="middle" fontSize="10" className="fill-slate-500">{t.v}</text>
            </g>
          ))}
          {/* línea */}
          <path d={pathD} fill="none" stroke={color} strokeWidth={2} />
          {/* puntos */}
          {points.map((p, idx) => (
            <circle key={idx} cx={p.x} cy={p.y} r={3} fill={color} />
          ))}
          {/* etiquetas X */}
          {points.map((p, idx) => (
            <text key={idx} x={p.x} y={plotH + 12} textAnchor="middle" fontSize="9" className="fill-slate-600">
              {p.label.length > 10 ? p.label.slice(0,9) + '…' : p.label}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}
